import { Widget } from "blue-widgets";

class CustomSelect extends Widget {
  constructor(el) {
    super(el);
    this.transitionDuration = null;
    this.setTransitionDuration = this.setTransitionDuration.bind(this);
    this.windowClickHandler = this.windowClickHandler.bind(this);
    this.init();
  }

  init() {
    this.trigger = this.el.querySelector("[data-select-trigger]");

    if (!this.trigger) {
      return;
    }
    this.selection = this.trigger.querySelector(".select__selection");
    this.label = this.trigger.querySelector(".select__label");
    this.content = this.el.querySelector("[data-select-options]");
    if (!this.content) {
      return;
    }
    const selectOptions = this.content.querySelectorAll("button");
    selectOptions.forEach((option) => {
      option.addEventListener("click", (e) => {
        e.preventDefault();
        this.selectionHandler(e);
      });
    });
    this.hiddenSelect = this.el.querySelector("[data-select-input]") || document.getElementById(this.options.targets);
    this.maxHeight = this.el.getAttribute("data-max") || 800;

    this.toggleContent = this.toggleContent.bind(this);

    this.trigger.addEventListener("click", (e) => {
      e.preventDefault();
      this.toggleContent();
    });

    this.el.addEventListener("close-expandable", () => {
      this.hideContent();
    });

    // Hide child components from keyboard focus initially
    if (this.content) {
      this.toggleContentTabindexs("-1");
      this.content.setAttribute("aria-hidden", "true");
    }

    // Set transition duration
    this.setTransitionDuration();
  }

  selectionHandler(e) {
    const target = e.target;
    const value = target.getAttribute("data-value");
    const label = e.target.innerHTML;

    if (this.hiddenSelect) {
      const optionToSelect = this.hiddenSelect.querySelector(
        `option[value="${value}"]`
      );

      if (optionToSelect) {
        const current = this.hiddenSelect.querySelector("[selected]");
        if (current) {
          current.removeAttribute("selected");
        }
        optionToSelect.setAttribute("selected", "");
      }
    }
    if (this.selection) {
      this.selection.innerHTML = label;
    }

    this.el.classList.add("has-value");
    this.toggleContent();

    const changeEvent = new Event("change", {
      bubbles: true,
      cancelable: true
    });

    this.hiddenSelect.dispatchEvent(changeEvent);
  }

  toggleContent() {
    const isExpanded = this.trigger.getAttribute("aria-expanded") === "true";
    if (isExpanded) {
      this.el.classList.remove("isOpen");
      window.removeEventListener("click", this.windowClickHandler);
      this.trigger.setAttribute("aria-expanded", "false");

      if (this.content) {
        this.toggleContentTabindexs("-1");
        this.content.setAttribute("aria-hidden", "true");
      }
    } else {
      this.el.classList.add("isOpen");
      window.addEventListener("click", this.windowClickHandler);
      this.trigger.setAttribute("aria-expanded", "true");

      if (this.content) {
        this.toggleContentTabindexs("0");
        this.content.setAttribute("aria-hidden", "false");
        this.focusTrap(true);
      }
    }

    if (this.transitionDuration) {
      this.content.style.overflowY = "hidden";
      setTimeout(() => {
        this.content.style.overflowY = "";
      }, this.transitionDuration);
    }

    const customEvent = new CustomEvent("expandable-toggled", {
      detail: {
        state: !this.isExpanded
      }
    });
    this.el.dispatchEvent(customEvent);

    if (
      this.label &&
      this.label.innerHTML !== this.label.getAttribute("data-label")
    ) {
      this.label.innerHTML = this.label.getAttribute("data-label");
    }
  }

  toggleContentTabindexs(tabIndex) {
    const tabbableElements = this.content.querySelectorAll(
      "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
    );

    const tabbableElementsArray = Array.from(tabbableElements);
    // Make the content focussable so they can tab into it even if there is only text
    this.content.setAttribute("tabindex", tabIndex);
    tabbableElementsArray.forEach((element) => {
      element.setAttribute("tabindex", tabIndex);
    });
  }

  setTransitionDuration() {
    const computedStyle = window.getComputedStyle(this.content);
    this.transitionDuration =
      parseFloat(computedStyle.getPropertyValue("transition-duration")) * 1000;
  }

  focusTrap(lock) {
    if (lock) {
      const tabbableElements = this.content.querySelectorAll(
        "button:not([disabled])"
      );
      const lastItem = tabbableElements[tabbableElements.length - 1];
      lastItem.addEventListener("blur", () => {
        this.trigger.focus({ focusVisible: true });
      });
    }
  }

  windowClickHandler(e) {
    const isOutsideSelect = e.target.closest(".select__input") === null;
    if (isOutsideSelect) {
      window.removeEventListener("click", this.windowClickHandler);
      this.hideContent();
    }
  }

  hideContent() {
    this.el.classList.remove("isOpen");
    this.trigger.setAttribute("aria-expanded", "false");

    if (this.content) {
      this.toggleContentTabindexs("-1");
      this.content.setAttribute("aria-hidden", "true");
    }
  }

  beforeRemove() {
    this.trigger.removeEventListener("click", this.toggleContent);
    this.el.removeEventListener("close-expandable", this.hideContent);
  }
}

export default CustomSelect;
